import { array2Object, catchError, ensureArray, jsonParse } from '@bigo/helpers';
import { createStore } from '@bigo/reactive-store';
import { sortBy } from 'lodash-es';

import { AuthType } from '@/constants/auth-type';
import { PredictType } from '@/constants/budget-control';

import businessDict from '../apis/business-dict';
import loginInfoStore from './login-info';

const groupIds = {
  product: 'WF_FYGS_BUDGET',
  department: 'WF_FYGS_DEPT',
  area: 'WF_FYGS_AREA',
  businessSubject: 'WF_BUSINESS_SUBJECT',
  budgetSubject: 'WF_INCOME',
  activityType: 'WF_BUDGET_ACTIVITY_TYPE',
  currencyType: 'BUDGET_CURRENCY_TYPE',
  expectedIdicator: 'BUDGET_EXPECTED_INCOME',
};

/** 业务字典项 */
export type BusinessDictItem = {
  id: string;
  name: string;
  groupId: string;
  parentCode: string;
  tag: string;
  showIndex: number;
  extData: string;
};

/** 业务字典下拉选项 */
export type BusinessDictOption = {
  value: string;
  label: string;
  raw: BusinessDictItem;
};

export type BusinessDictState = {
  /** 业务 */
  rawProducts: BusinessDictItem[];
  /** 组别 */
  rawDepartments: BusinessDictItem[];
  /** 地区 */
  rawAreas: BusinessDictItem[];
  /** 业务科目 */
  rawBusinessSubjects: BusinessDictItem[];
  /** 财务科目 */
  rawBudgetSubjects: BusinessDictItem[];
  /** 活动类型 */
  rawActivityTypes: BusinessDictItem[];
  /** 预算币种 */
  rawCurrencyTypes: BusinessDictItem[];
  /** 预期收益 */
  rawExpectedIdicator: BusinessDictItem[];
};

/** 转化为下拉选项 */
const toOptions = (items: BusinessDictItem[]): BusinessDictOption[] =>
  items.map((item) => ({ value: item.id, label: item.name, raw: item }));

const split = (value: string | string[]): string[] =>
  typeof value === 'string' ? value.split(',') : value;

const businessDictStore = createStore({
  state: {
    loading: true,
    rawProducts: [],
    rawDepartments: [],
    rawAreas: [],
    rawBusinessSubjects: [],
    rawBudgetSubjects: [],
    rawActivityTypes: [],
    rawCurrencyTypes: [],
    rawExpectedIdicator: [],
  } as BusinessDictState,
  getters: {
    // ===== 下拉选项 =====
    /** 业务 */
    products() {
      return toOptions(this.rawProducts);
    },
    /** 业务 */
    productMap() {
      return array2Object(this.products, 'value');
    },
    /** 组别 */
    departments() {
      return toOptions(this.rawDepartments);
    },
    /** 组别 */
    departmentMap() {
      return array2Object(this.departments, 'value');
    },
    /** 地区 */
    areas() {
      return toOptions(this.rawAreas);
    },
    /** 地区 */
    areaMap() {
      return array2Object(this.areas, 'value');
    },
    /** 业务科目 */
    businessSubjects() {
      return toOptions(this.rawBusinessSubjects);
    },
    /** 业务科目 */
    businessSubjectsMap() {
      return array2Object(this.businessSubjects, 'value');
    },
    /** 财务科目 */
    budgetSubjects() {
      return toOptions(this.rawBudgetSubjects);
    },
    /** 财务科目 */
    budgetSubjectMap() {
      return array2Object(this.budgetSubjects, 'value');
    },
    /** 活动类型 */
    activityTypes() {
      return toOptions(this.rawActivityTypes);
    },
    /** 活动类型 */
    activityTypeMap() {
      return array2Object(this.activityTypes, 'value');
    },
    /** 预算币种 */
    currencyTypes() {
      return toOptions(this.rawCurrencyTypes);
    },
    /** 预算币种 */
    currencyTypeMap() {
      return array2Object(this.currencyTypes, 'value');
    },
    /** 我的业务 */
    myProducts() {
      return this.products.filter((i) => loginInfoStore.businessPermission.has(i.value));
    },
    /** 我的业务-高级权限 */
    myAdvancedProducts() {
      return this.myProducts.filter(
        (i) => loginInfoStore.businessPermission.get(i.value)?.authType === AuthType.High,
      );
    },
    /** 我的组别 */
    myDepartments() {
      return this.departments.filter((i) => {
        // 获取当前用户业务权限
        const record = loginInfoStore.businessPermission.get(i.raw.parentCode);
        if (!record) return false;

        if (record.departments === 'ALL') return true;
        if (record.departments.has(i.value)) return true;

        return false;
      });
    },
    /** 我的地区 */
    myAreas() {
      return this.areas.filter((i) => {
        // 获取当前用户业务权限
        const record = loginInfoStore.businessPermission.get(i.raw.parentCode);
        if (!record) return false;

        if (record.areas === 'ALL') return true;
        if (record.areas.has(i.value)) return true;

        return false;
      });
    },
    /** 预期收益 */
    expectedIdicator() {
      return toOptions(this.rawExpectedIdicator);
    },
    /** 预期收益 */
    expectedIdicatorMap() {
      return array2Object(this.expectedIdicator, 'value');
    },
  },
  actions: {
    /** 批量加载数据字典 */
    async load() {
      const [, result] = await catchError<BusinessDictItem[][]>(
        Promise.all([
          businessDict.listDict(groupIds.product),
          businessDict.listDict(groupIds.department),
          businessDict.listDict(groupIds.area),
          businessDict.listDict(groupIds.businessSubject),
          businessDict.listDict(groupIds.budgetSubject),
          businessDict.listDict(groupIds.activityType),
          businessDict.listDict(groupIds.currencyType),
          businessDict.listDict(groupIds.expectedIdicator),
        ]),
      );

      if (!result) return;

      this.rawProducts = sortBy(result[0], ['showIndex']);
      this.rawDepartments = sortBy(result[1], ['showIndex']);
      this.rawAreas = sortBy(result[2], ['showIndex']);
      this.rawBusinessSubjects = sortBy(result[3], ['showIndex']);
      this.rawBudgetSubjects = sortBy(result[4], ['showIndex']);
      this.rawActivityTypes = sortBy(result[5], ['showIndex']);
      this.rawCurrencyTypes = sortBy(result[6], ['showIndex']);
      this.rawExpectedIdicator = sortBy(result[7], ['showIndex']);
    },
    /** 根据id获取业务 */
    getProduct(id: string) {
      return this.productMap[id];
    },
    /** 根据id获取业务label */
    getProductLabel(id: string) {
      return this.getProduct(id)?.label;
    },
    /** 根据ids获取业务labels */
    getProductLabels(ids: string) {
      return split(ids)
        .map((id) => this.getProductLabel(id))
        .join(',');
    },
    /** 根据id组别 */
    getDepartment(id: string) {
      return this.departmentMap[id];
    },
    /** 根据id组别label */
    getDepartmentLabel(id: string) {
      return this.getDepartment(id)?.label;
    },
    /** 根据ids获取组别labels */
    getDepartmentLabels(ids: string) {
      return split(ids)
        .map((id) => this.getDepartmentLabel(id))
        .join(',');
    },
    /** 根据id地区 */
    getArea(id: string) {
      return this.areaMap[id];
    },
    /** 根据id地区label */
    getAreaLabel(id: string) {
      return this.getArea(id)?.label;
    },
    /** 根据ids获取地区labels */
    getAreaLabels(ids: string) {
      return split(ids)
        .map((id) => this.getAreaLabel(id))
        .join(',');
    },
    /** 根据id业务科目 */
    getBusinessSubject(id: string) {
      return this.businessSubjectsMap[id];
    },
    /** 根据id业务科目label */
    getBusinessSubjectLabel(id: string) {
      return this.getBusinessSubject(id)?.label;
    },
    /** 根据ids获取业务科目labels */
    getBusinessSubjectLabels(ids: string) {
      return split(ids)
        .map((id) => this.getBusinessSubjectLabel(id))
        .join(',');
    },
    /** 根据id财务科目 */
    getBudgetSubject(id: string) {
      return this.budgetSubjectMap[id];
    },
    /** 根据id财务科目label */
    getBudgetSubjectLabel(id: string) {
      return this.getBudgetSubject(id)?.label;
    },
    /** 根据ids获取财务科目labels */
    getBudgetSubjectLabels(ids: string) {
      return split(ids)
        .map((id) => this.getBudgetSubjectLabel(id))
        .join(',');
    },
    /** 判断是否为纳入预算管控的财务科目 */
    isControlledBudgetSubject(id: string) {
      return this.getBudgetSubject(id)?.raw.tag === '1';
    },
    /** 根据id获取活动类型 */
    getActivityType(id: string) {
      return this.activityTypeMap[id];
    },
    /** 根据id获取活动类型label */
    getgetActivityTypeLabel(id: string) {
      return this.getActivityType(id)?.label;
    },
    /** 根据ids获取活动类型labels */
    getgetActivityTypeLabels(ids: string) {
      return split(ids)
        .map((id) => this.getgetActivityTypeLabel(id))
        .join(',');
    },

    /** 根据id获取预算币种 */
    getCurrencyType(id: string) {
      return this.activityTypeMap[id];
    },
    /** 根据id获取预算币种label */
    getCurrencyTypeLabel(id: string) {
      return this.getCurrencyType(id)?.label;
    },
    /** 根据ids获取预算币种labels */
    getCurrencyTypeLabels(ids: string) {
      return split(ids)
        .map((id) => this.getCurrencyTypeLabel(id))
        .join(',');
    },

    /** 根据id获取预期收益 */
    getExpectedIdicator(id: string) {
      return this.expectedIdicatorMap[id];
    },
    /** 根据id获取预期收益label */
    getExpectedIdicatorLabel(id: string) {
      return this.getExpectedIdicator(id)?.label;
    },
    /** 获取"地区"和"组别"为"全部"的我的业务 */
    getAllDepartAreasOfProducts() {
      return this.myProducts.filter((product) => {
        const item = loginInfoStore.businessPermission.get(product.value);
        return item?.departments === 'ALL' && item?.areas === 'ALL';
      });
    },
  },
});

/** 根据parentCode筛选选项列表 */
export function filterByParentCode(options: BusinessDictOption[], parentCode?: string) {
  return parentCode ? options.filter((o) => o.raw.parentCode === parentCode) : [];
}

/** 根据departmentCode筛选选项列表 */
export function filterArea(options: BusinessDictOption[], departmentCodes?: string | string[]) {
  if (!departmentCodes) return [];

  departmentCodes = ensureArray(departmentCodes);

  const areaCodeSet = new Set();
  const areaOptions: BusinessDictOption[] = [];

  departmentCodes.forEach((departmentCode) => {
    const extData = businessDictStore.getDepartment(departmentCode)?.raw.extData || '[]';
    const extDataSet = new Set(JSON.parse(extData.replace(/↵/g, '')));
    options.forEach((item) => {
      if (extDataSet.has(item.value) && !areaCodeSet.has(item.value)) {
        areaCodeSet.add(item.value);
        areaOptions.push(item);
      }
    });
  });

  return areaOptions;
}

/** 根据productCode筛选财务科目为预测支出 */
export function filterExpendBudgetSubject(list: BusinessDictOption[], productCode: string) {
  return list.filter((item) => {
    const extData = jsonParse<any>(item.raw.extData);
    if (!extData) return false;
    return extData.incomeAndCostProducts[productCode] === `${PredictType.EXPEND}`;
  });
}

/** 构建业务、组别、地区关系数据 */
export function buildProductDeptAreaList(
  // 业务
  products: BusinessDictOption[],
  // 组别
  departments: BusinessDictOption[],
  // 区域
  areas: BusinessDictOption[],
): string[][] {
  const result: string[][] = [];
  products.forEach((product) => {
    const filterDepts = filterByParentCode(departments, product.raw.id);
    filterDepts.forEach((dept) => {
      const filterAreas = filterArea(areas, dept.raw.id);
      filterAreas.forEach((area) => {
        result.push([product.label, dept.label, area.label]);
      });
    });
  });
  return result;
}

/** 构建业务、业务科目、财务关系数据 */
export function buildProductBusBudgetList(
  // 业务
  products: BusinessDictOption[],
  // 业务科目
  businessSubjects: BusinessDictOption[],
  // 财务科目
  budgetSubjects: BusinessDictOption[],
): string[][] {
  const result: string[][] = [];
  products.forEach((product) => {
    const filterBusiness = filterByParentCode(businessSubjects, product.raw.id);
    filterBusiness.forEach((business) => {
      const filterBudget = budgetSubjects.filter((item) => item.value === business.raw.tag);
      filterBudget.forEach((budget) => {
        result.push([product.label, business.label, budget.label]);
      });
    });
  });
  return result;
}

export default businessDictStore;
